/**
 * Where the executive dashboard opens (GP's ask, 2026-10-27): this month up to
 * its last day with data, against the same days of last month, with only the
 * own brand picked in Competitive intelligence's Brands chip. Nothing they pick
 * is saved.
 *
 * Why not up to today: the newest days can still be loading, and a day with no
 * data set against a full one makes this month look worse than it is (seen on
 * staging: the line fell to zero on Oct 26–27). So the dashboard first reads
 * this month's daily volume across every operator and ends on the last day
 * that has any.
 */
import { skipToken } from '@reduxjs/toolkit/query';

import type { SourceId, TimeSeries } from '@/data-sources/contract';
import type { IsoDate } from '@/lib/analytics/dates';
import { comparisonFor, monthToDate } from '@/lib/analytics/periods';
import type { FilterState } from '@/store/slices/filtersSlice';

import { widgetsApi } from '../widgets/widgets.api';

/** The last day of a daily series with any mention, or `null` when none has one. */
export function lastDayWithData(volume: TimeSeries): IsoDate | null {
  if (volume.bucket !== 'day') return null;
  for (let i = volume.x.length - 1; i >= 0; i--) {
    if (volume.series.some((s) => (s.values[i] ?? 0) > 0)) return volume.x[i] ?? null;
  }
  return null;
}

export function executiveFilters(
  today: IsoDate,
  ownBrand: string,
  lastDay: IsoDate | null = null,
): FilterState {
  const start = monthToDate(today).start!;
  // A last day outside this month (none yet this month) leaves the range at today.
  const end = lastDay && lastDay >= start && lastDay < today ? lastDay : today;
  const range = { start, end };
  return {
    range,
    compare: comparisonFor(range),
    // A workspace with no own brand starts with every brand rather than none.
    selections: ownBrand ? { brand: [ownBrand] } : {},
  };
}

/**
 * This month's last day with data, read once before the executive filters
 * are seeded. `settled` is false while it loads; a failed read settles on
 * `null`, and the dashboard then runs to today.
 */
export function useLastDayWithData(
  source: SourceId,
  today: IsoDate | undefined,
  enabled: boolean,
): { settled: boolean; day: IsoDate | null } {
  const result = widgetsApi.useWidgetQuery(
    enabled && today
      ? {
          source,
          params: {
            id: 'mention-volume-vs-competitors',
            query: { range: monthToDate(today), compare: null, selections: {} },
          },
        }
      : skipToken,
  );
  if (!enabled) return { settled: true, day: null };
  if (result.isError) return { settled: true, day: null };
  if (!result.data) return { settled: false, day: null };
  const data = result.data as { volume: TimeSeries };
  return { settled: true, day: lastDayWithData(data.volume) };
}
